var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import "./questionnaireHistory.css";
import NavBar from "../../organisms/navBar/navBar";
import PageHeader from "../../organisms/pageHeader/pageHeader";
import Bar from "../../molecules/bar/bar";
import Card from "../../atoms/card/card";
import DashboardGraps from "../dashboard/dashboardGraps";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getQuestionnaireOneHistory, getQuestionnaireTwoHistory, getQuestionnaireMilestoneHistory, } from "../../../redux/actions/questionnaireHistoryActions";
import TableCard from "../../molecules/table/table";
import { createSelector } from "@reduxjs/toolkit";
import VerticalTab from "../../molecules/verticalTab/verticalTab";
/**
 * This returns the ordinals for a given date.
 * 1 becomes st
 * 2 becomes nd
 * etc...
 * @param {number} number The date.
 */
function nthNumber(number) {
    return number > 0
        ? ["th", "st", "nd", "rd"][(number > 3 && number < 21) || number % 10 > 3 ? 0 : number % 10]
        : "";
}
var selectQuestionnaireHistory = function (state) {
    var reducer = state.rootReducer
        .questionnaireHistoryReducer;
    return (reducer || {
        responses: {},
        graphData: {},
        score: {},
    });
};
var selectDashboard = function (state) {
    return state.rootReducer.dashboardReducer;
};
var selectHistoryData = createSelector([selectQuestionnaireHistory], function (history) { return ({
    responses: history.responses,
    graphData: history.graphData,
    score: history.score,
}); });
var selectDashboardData = createSelector([selectDashboard], function (dashboard) { return ({
    viewing_username: dashboard.viewing_username || "",
    logged_in_user_type: dashboard.logged_in_user_type || "",
    viewing_user_type: dashboard.viewing_user_type || "",
}); });
var processResponse = function (section, data, tableData) {
    var _loop_1 = function (key) {
        var value = data[key];
        if (typeof value === "object") {
            if (Array.isArray(value)) {
                // Handle array values
                value.forEach(function (item, index) {
                    var question = "".concat(key, " - ").concat(index + 1);
                    tableData.push([
                        section,
                        question,
                        Array.isArray(item) ? item.join(", ") : item.toString(),
                    ]);
                });
            }
            else {
                // Recursively process nested objects
                processResponse("".concat(section, " - ").concat(key), value, tableData);
            }
        }
        else {
            // Handle scalar values
            tableData.push([section, key, value.toString()]);
        }
    };
    for (var key in data) {
        _loop_1(key);
    }
};
function QuestionnaireResponses() {
    var dateObj = new Date();
    var day = dateObj.getDate();
    var month = dateObj.toLocaleString("default", { month: "long" });
    var year = dateObj.getFullYear();
    var dispatch = useDispatch();
    var questionnaireNumber = useParams().questionnaireNumber;
    var _a = useSelector(selectDashboardData), viewing_username = _a.viewing_username, logged_in_user_type = _a.logged_in_user_type, viewing_user_type = _a.viewing_user_type;
    var _b = useSelector(selectHistoryData), responses = _b.responses, graphData = _b.graphData, score = _b.score;
    var _c = useState(0), setTabNumber = _c[1];
    var _d = useState(""), searchQuery = _d[0], setSearchQuery = _d[1];
    var tabItems = useMemo(function () {
        var keys = Object.keys(responses !== null && responses !== void 0 ? responses : {});
        console.log("Tab keys: ", keys);
        return questionnaireNumber === "1" || questionnaireNumber === "milestone"
            ? keys.filter(function (key) { return key !== "Adviser impact"; })
            : keys;
    }, [responses, questionnaireNumber]);
    var LABEL_MAPPING = {
        "Searching for work": "Job Seeking Behaviours",
        "Your obligations": "Your Expectations",
        "Adviser obligations": "Adviser Expectations",
    };
    useEffect(function () {
        tableData.length = 0;
        if (!viewing_username)
            return;
        switch (questionnaireNumber) {
            case "1":
                dispatch(getQuestionnaireOneHistory(viewing_username));
                break;
            case "milestone":
                // The progression milestone attempt - the middle submission, once
                // one exists. Not numbered "Questionnaire 2" since it's skippable.
                dispatch(getQuestionnaireMilestoneHistory(viewing_username));
                break;
            case "2":
                // Questionnaire 2 is always the final/newest completed submission -
                // unchanged from before the milestone attempt existed.
                dispatch(getQuestionnaireTwoHistory(viewing_username));
                break;
        }
    }, [questionnaireNumber, dispatch, viewing_username]);
    // Iterate over each section (Adviser Obligations, Your Obligations)
    var tableData = useMemo(function () {
        var arr = [];
        for (var section in responses) {
            var sectionData = responses[section];
            processResponse(section, sectionData, arr);
        }
        return arr;
    }, [responses]);
    // Only rows belonging to a visible category tab.
    var visibleRows = useMemo(function () {
        return tableData
            .filter(function (row) { return tabItems.includes(row[0]); })
            // Demographics has questions that only apply based on a previous answer
            // (e.g. "How well can you speak English?" only applies if the jobseeker's
            // main language isn't English) - unanswered ones default to blank or '0',
            // so hide them here rather than showing a meaningless response.
            .filter(function (row) {
            return row[0] !== "Demographics" || (row[2] !== "" && row[2] !== "0");
        });
    }, [tableData, tabItems]);
    var tabPanels = tabItems.map(function (tab, index) {
        var filteredData = visibleRows
            .filter(function (row) { return row[0] === tab; })
            .map(function (row) { return [row[1], row[2]]; });
        return (_jsx("div", __assign({ className: "scrollable-table-container" }, { children: _jsx(TableCard, { data: filteredData, headers: ["Questions", ""], currentTab: index, rowsPerPage: filteredData.length, hidePagination: true }) }), tab));
    });
    var query = searchQuery.trim().toLowerCase();
    var searchResults = visibleRows
        .filter(function (row) {
        return row[1].toLowerCase().includes(query) ||
            row[2].toLowerCase().includes(query);
    })
        .map(function (row) { var _a; return [(_a = LABEL_MAPPING[row[0]]) !== null && _a !== void 0 ? _a : row[0], row[1], row[2]]; });
    return (_jsxs("div", __assign({ className: "h-full" }, { children: [_jsx(NavBar, { viewing_user: viewing_user_type, logged_in_user: logged_in_user_type, viewing_username: viewing_username }), _jsxs("div", __assign({ className: "questionnaire-page-template-right" }, { children: [_jsx(PageHeader, { title: questionnaireNumber === "milestone"
                            ? "Progression Milestone History"
                            : "Questionnaire ".concat(questionnaireNumber, " History"), backButton: "Back to History", backTo: "questionnaire" }), _jsx(Bar, __assign({ title: "Employability Area", className: "my-3 py-2" }, { children: _jsx(Card, { children: "".concat(day).concat(nthNumber(day), " ").concat(month, " ").concat(year) }) })), _jsx(DashboardGraps, { data: graphData, rightSide: score, userType: viewing_user_type }), _jsx(Bar, { title: "Your Answers", className: "my-5 py-4" }), _jsx("div", __assign({ className: "filter-search-wrapper" }, { children: _jsxs("div", __assign({ className: "filter-search-bar" }, { children: [_jsxs("svg", __assign({ className: "filter-search-icon", width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg" }, { children: [_jsx("circle", { cx: "11", cy: "11", r: "8", stroke: "currentColor", strokeWidth: "2" }), _jsx("path", { d: "M21 21L16.65 16.65", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round" })] })), _jsx("input", { className: "filter-search-input", placeholder: "Search all categories...", "aria-label": "Search all categories", value: searchQuery, onChange: function (e) { return setSearchQuery(e.target.value); } }), searchQuery !== "" && (_jsx("button", __assign({ className: "filter-clear-btn", "aria-label": "Clear search", onClick: function () { return setSearchQuery(""); } }, { children: "\u00D7" })))] })) })), _jsx("div", __assign({ className: "responses-answers-area" }, { children: query === "" ? (_jsx(VerticalTab, { setStateCallback: setTabNumber, titles: tabItems, label_mapping: LABEL_MAPPING, 
                            // eslint-disable-next-line react/no-children-prop
                            children: tabPanels })) : searchResults.length > 0 ? (_jsx("div", __assign({ className: "scrollable-table-container" }, { children: _jsx(TableCard, { data: searchResults, headers: ["Category", "Questions", ""], rowsPerPage: searchResults.length, hidePagination: true }) }))) : (_jsx("p", __assign({ className: "p-4" }, { children: "No questions or answers match your search." }))) }))] }))] })));
}
export default QuestionnaireResponses;
